import { useCallback, useEffect, useMemo, useState } from "react";
import { Search, Skeleton } from "@vibe/core";
import { SyncWizardBoardIcon } from "./SyncWizardBoardIcon";
import { SyncWizardGroupSelector } from "./SyncWizardGroupSelector";
import { WorkspaceType } from "types/workspace.list.interface";
import { WorkspaceIcon } from "@components/addItem/WorkspaceList";
import { MondayColors } from "@data/mondayColors";
import { useWorkspaceBoards } from "@hooks/api";
import { BoardsByWorkspaceMap } from "@hooks/api/useWorkspaceBoards";
import { cn } from "@utils/tailwind-merge";
import ButtonComponent from "@components/Button";
import { useSyncWizardStep2ViewModel } from "./SyncWizardContext";
import type { WizardBoardTarget, WizardGroupTarget } from "./syncWizard.types";

const normalizeForSearch = (value: string): string =>
  value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\p{L}\p{N}]+/gu, "")
    .toLowerCase();

const matchesSearch = (haystack: string, needle: string): boolean => {
  const normalizedNeedle = normalizeForSearch(needle);
  if (!normalizedNeedle) return true;
  return normalizeForSearch(haystack).includes(normalizedNeedle);
};

const BOARD_SKELETON_ROW_COUNT = 6;
const WORKSPACE_SKELETON_CARD_COUNT = 6;
const WORKSPACE_SKELETON_LABEL_WIDTH = 120;

const BoardRowSkeleton = ({ isLast }: { isLast: boolean }) => (
  <div
    className={cn(
      "flex items-center gap-2.5 px-3 py-2.5",
      !isLast && "border-b border-t-0 border-x-0 border-solid border-[#f0f0f0]",
    )}
  >
    <div className="flex flex-shrink-0 justify-center items-center w-8 h-8">
      <Skeleton type="rectangle" width={32} height={32} />
    </div>
    <div className="flex-1 min-w-0">
      <Skeleton type="text" width={160} height={14} />
    </div>
  </div>
);

const WorkspaceCardSkeleton = () => (
  <div
    className="flex gap-3 items-center px-3 py-3 w-full rounded-xl border border-solid border-[#e6e9ef] bg-white"
    aria-hidden
  >
    <Skeleton type="rectangle" width={44} height={44} />
    <div className="flex-1 min-w-0">
      <Skeleton type="text" width={WORKSPACE_SKELETON_LABEL_WIDTH} height={14} />
    </div>
  </div>
);

function QueryErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="px-3 py-8">
      <div className="flex flex-col gap-2 items-center text-center">
        <p className="text-[12px] text-[#676879] !mb-0">{message}</p>
        <ButtonComponent text="Retry" className="retry-button" onClick={onRetry} />
      </div>
    </div>
  );
}

export function SyncWizardStep2() {
  const {
    currentBoard,
    workspaceListdata,
    isWorkspacesLoading,
    selectedWorkspace,
    setSelectedWorkspace,
    targetBoard,
    setTargetBoard,
    targetGroup,
    setTargetGroup,
    processSubitems,
    processUpdates,
    setProcessSubitems,
    setProcessUpdates,
    hasWorkspaceError,
    onRetryWorkspaces,
  } = useSyncWizardStep2ViewModel();

  const selectedWsObj = workspaceListdata.find((w) => w.id === selectedWorkspace) ?? null;

  const {
    boardsByWorkspace,
    fetchBoardsForWorkspace,
    loadingWorkspaceId,
    error: boardsError,
    mutateWorkspaceBoards,
  } = useWorkspaceBoards();

  const isLoadingBoardsForSelected =
    !!selectedWorkspace && loadingWorkspaceId === selectedWorkspace;
  const hasBoardsError = !!boardsError && !isLoadingBoardsForSelected;

  const handleRetryBoards = useCallback(() => {
    void mutateWorkspaceBoards();
  }, [mutateWorkspaceBoards]);

  useEffect(() => {
    if (selectedWorkspace) {
      fetchBoardsForWorkspace(selectedWorkspace);
    }
  }, [selectedWorkspace, fetchBoardsForWorkspace]);

  const handleWorkspaceSelect = (workspaceId: string) => {
    setSelectedWorkspace(workspaceId);
    setTargetBoard(null);
    setTargetGroup(null);
  };

  const handleBackToWorkspaces = () => {
    setSelectedWorkspace(null);
    setTargetBoard(null);
    setTargetGroup(null);
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <p className="text-[12px] text-[#676879] !mb-4 flex-shrink-0">
        {!selectedWorkspace
          ? "Select a workspace to continue."
          : !targetBoard
            ? "Select a board to continue."
            : "Choose where these items should be shared to."}
      </p>

      {!selectedWorkspace ? (
        <WorkspaceSelector
          workspaces={workspaceListdata}
          isLoading={isWorkspacesLoading}
          onSelect={handleWorkspaceSelect}
          hasError={hasWorkspaceError}
          onRetry={onRetryWorkspaces}
        />
      ) : (
        <BoardSelector
          currentBoard={currentBoard}
          selectedWsObj={selectedWsObj}
          boardsByWorkspace={boardsByWorkspace}
          isLoadingBoards={isLoadingBoardsForSelected}
          targetBoard={targetBoard}
          setTargetBoard={setTargetBoard}
          targetGroup={targetGroup}
          setTargetGroup={setTargetGroup}
          onBack={handleBackToWorkspaces}
          processSubitems={processSubitems}
          processUpdates={processUpdates}
          setProcessSubitems={setProcessSubitems}
          setProcessUpdates={setProcessUpdates}
          hasError={hasBoardsError}
          onRetry={handleRetryBoards}
        />
      )}
    </div>
  );
}

function WorkspaceSelector({
  workspaces,
  isLoading,
  onSelect,
  hasError,
  onRetry,
}: {
  workspaces: WorkspaceType[];
  isLoading: boolean;
  onSelect: (workspaceId: string) => void;
  hasError: boolean;
  onRetry: () => void;
}) {
  const [hoveredWs, setHoveredWs] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const filteredWorkspaces = useMemo(
    () => workspaces.filter((w) => matchesSearch(w.name, search)),
    [workspaces, search],
  );

  const showSkeletons = isLoading && workspaces.length === 0;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex-shrink-0 mb-3">
        <Search placeholder="Search workspaces" onChange={setSearch} size="small" value={search} />
      </div>

      {showSkeletons ? (
        <div className="grid grid-cols-2 auto-rows-min gap-2 flex-1 overflow-hidden min-h-0 pr-0.5">
          {Array.from({ length: WORKSPACE_SKELETON_CARD_COUNT }).map((_, idx) => (
            <WorkspaceCardSkeleton key={`workspace-skeleton-${idx}`} />
          ))}
        </div>
      ) : hasError ? (
        <QueryErrorState message="We couldn't load workspaces." onRetry={onRetry} />
      ) : filteredWorkspaces.length === 0 ? (
        <div className="py-8 text-center text-[12px] text-[#676879] flex-shrink-0">
          {search ? `No workspaces match "${search}"` : "No workspaces available"}
        </div>
      ) : (
        <div className="grid grid-cols-2 auto-rows-min gap-2 flex-1 overflow-y-auto min-h-0 pr-0.5">
          {filteredWorkspaces.map((workspace, i) => {
            const isHovered = hoveredWs === workspace.id;
            return (
              <div
                key={workspace.id}
                onClick={() => onSelect(workspace.id)}
                onMouseEnter={() => setHoveredWs(workspace.id)}
                onMouseLeave={() => setHoveredWs(null)}
                className="flex gap-3 items-center px-3 py-3 w-full text-left rounded-xl border border-solid transition-all cursor-pointer"
                style={{
                  borderColor: "#e6e9ef",
                  backgroundColor: isHovered ? "#f6f7fb" : "white",
                }}
              >
                <WorkspaceIcon workspace={workspace} index={i} containerSize={44} iconSize={24} />
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] text-[#323338] truncate" style={{ fontWeight: 600 }}>
                    {workspace.name}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BoardSelector({
  currentBoard,
  selectedWsObj,
  boardsByWorkspace,
  isLoadingBoards,
  targetBoard,
  setTargetBoard,
  targetGroup,
  setTargetGroup,
  onBack,
  processSubitems,
  processUpdates,
  setProcessSubitems,
  setProcessUpdates,
  hasError,
  onRetry,
}: {
  currentBoard: number;
  selectedWsObj: WorkspaceType | null;
  boardsByWorkspace: BoardsByWorkspaceMap;
  isLoadingBoards: boolean;
  targetBoard: WizardBoardTarget | null;
  setTargetBoard: (targetBoard: WizardBoardTarget | null) => void;
  targetGroup: WizardGroupTarget | null;
  setTargetGroup: (targetGroup: WizardGroupTarget | null) => void;
  onBack: () => void;
  processSubitems: boolean;
  processUpdates: boolean;
  setProcessSubitems: (value: boolean) => void;
  setProcessUpdates: (value: boolean) => void;
  hasError: boolean;
  onRetry: () => void;
}) {
  const [boardSearch, setBoardSearch] = useState("");
  const [hoveredBoard, setHoveredBoard] = useState<string | null>(null);

  const workspaceBoards = useMemo(
    () => boardsByWorkspace[selectedWsObj?.id ?? ""] ?? [],
    [boardsByWorkspace, selectedWsObj?.id],
  );

  const filteredBoards = useMemo(
    () =>
      workspaceBoards.filter(
        (b) => matchesSearch(b.name, boardSearch) && b.id !== currentBoard.toString(),
      ),
    [workspaceBoards, boardSearch, currentBoard],
  );

  const selectedBoardIndex = workspaceBoards.findIndex((b) => b.id.toString() === targetBoard?.id);
  const selectedBoardObj =
    selectedBoardIndex >= 0 ? workspaceBoards[selectedBoardIndex] : undefined;
  const selectedBoardColor =
    targetBoard?.color ??
    MondayColors[(selectedBoardIndex >= 0 ? selectedBoardIndex : 0) % MondayColors.length];
  // Fallback to Monday brand blue when the workspace has no icon configured
  const selectedWsColor = selectedWsObj?.settings?.icon?.color ?? "#0073ea";

  const handleBack = () => {
    setBoardSearch("");
    onBack();
  };

  const handleChangeBoard = () => {
    setTargetBoard(null);
    setTargetGroup(null);
  };

  const handleBoardSelect = (board: WizardBoardTarget) => {
    setTargetBoard(board);
    setTargetGroup(null);
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div
        onClick={handleBack}
        className="flex items-center gap-1.5 text-[11px] text-[#0073ea] mb-3 hover:underline cursor-pointer flex-shrink-0"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
          <path
            d="M15 18l-6-6 6-6"
            stroke="#0073ea"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        All workspaces
      </div>

      <div className="flex items-center gap-2.5 mb-4 flex-shrink-0">
        <div
          className="flex flex-shrink-0 justify-center items-center w-7 h-7 text-white rounded-lg"
          style={{ backgroundColor: selectedWsColor, fontSize: "12px", fontWeight: 700 }}
        >
          <WorkspaceIcon workspace={selectedWsObj!} index={0} containerSize={28} iconSize={20} />
        </div>
        <span className="text-[13px] font-semibold text-[#323338]">{selectedWsObj?.name}</span>
      </div>

      {!targetBoard ? (
        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex-shrink-0 mb-3">
            <Search
              placeholder="Search boards"
              onChange={setBoardSearch}
              size="small"
              value={boardSearch}
              autoFocus
            />
          </div>

          {isLoadingBoards ? (
            <div className="flex flex-col flex-1 overflow-hidden min-h-0 pr-0.5 border border-solid border-[#e6e9ef] rounded-lg">
              {Array.from({ length: BOARD_SKELETON_ROW_COUNT }).map((_, idx) => (
                <BoardRowSkeleton
                  key={`board-skeleton-${idx}`}
                  isLast={idx === BOARD_SKELETON_ROW_COUNT - 1}
                />
              ))}
            </div>
          ) : hasError ? (
            <div className="border border-solid border-[#e6e9ef] rounded-lg">
              <QueryErrorState
                message="We couldn't load boards for this workspace."
                onRetry={onRetry}
              />
            </div>
          ) : filteredBoards.length === 0 ? (
            <div className="py-8 text-center text-[12px] text-[#676879] flex-shrink-0">
              {boardSearch ? `No boards match "${boardSearch}"` : "No boards in this workspace"}
            </div>
          ) : (
            <div className="flex flex-col flex-1 overflow-y-auto min-h-0 pr-0.5 border border-solid border-[#e6e9ef] rounded-lg">
              {filteredBoards.map((board, i) => {
                const isHovered = hoveredBoard === board.id;
                const isLast = i === filteredBoards.length - 1;
                const boardColor = MondayColors[i % MondayColors.length];
                return (
                  <div
                    key={board.id}
                    onClick={() =>
                      handleBoardSelect({
                        id: board.id.toString(),
                        name: board.name,
                        color: boardColor,
                      })
                    }
                    onMouseEnter={() => setHoveredBoard(board.id.toString() ?? null)}
                    onMouseLeave={() => setHoveredBoard(null)}
                    className={cn(
                      "flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors cursor-pointer",
                      !isLast && "border-b border-t-0 border-x-0 border-solid border-[#f0f0f0]",
                    )}
                    style={{
                      backgroundColor: isHovered ? "#f6f7fb" : "white",
                    }}
                  >
                    <div
                      className="flex flex-shrink-0 justify-center items-center w-8 h-8 rounded-lg"
                    style={{ backgroundColor: boardColor + "22" }}
                    >
                    <SyncWizardBoardIcon color={boardColor} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p
                        className="text-[12px] text-[#323338] truncate"
                        style={{ fontWeight: 600, lineHeight: "1.3" }}
                      >
                        {board.name}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : selectedBoardObj ? (
        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-solid border-[#0073ea] bg-[#f0f7ff] mb-4 flex-shrink-0">
            <div
              className="flex flex-shrink-0 justify-center items-center w-8 h-8 rounded-lg"
              style={{ backgroundColor: selectedBoardColor + "22" }}
            >
              <SyncWizardBoardIcon color={selectedBoardColor} />
            </div>
            <span className="text-[12px] font-semibold text-[#323338] flex-1 truncate">
              {selectedBoardObj.name}
            </span>
            <div
              role="button"
              onClick={handleChangeBoard}
              className="text-[11px] text-[#0073ea] hover:underline flex-shrink-0 cursor-pointer"
            >
              Change
            </div>
          </div>

          <div className="overflow-y-auto flex-1 min-h-0">
            <SyncWizardGroupSelector
              targetBoard={targetBoard}
              boardName={selectedBoardObj.name}
              boardColor={selectedBoardColor}
              targetGroup={targetGroup}
              setTargetGroup={setTargetGroup}
              processSubitems={processSubitems}
              processUpdates={processUpdates}
              setProcessSubitems={setProcessSubitems}
              setProcessUpdates={setProcessUpdates}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
